import axios, { AxiosHeaders, type InternalAxiosRequestConfig } from 'axios';
import { createCsrfAwareAxiosInstance } from 'src/api/common/http/createCsrfAwareAxiosInstance';
import type { ApiEnvelope } from 'src/api/common/api/ApiEnvelope';
import { getApiBaseUrl } from 'src/api/config/apiBaseUrl';
import type { ErisUser } from 'src/api/eris/store/ErisUserStore';

const PROTECTED_ER_PATHS = [
  '/api/er/users/me',
  '/api/er/groups',
  '/api/er/disasters',
  '/api/er/workplaces',
];

const authAxiosInstance = createCsrfAwareAxiosInstance(getApiBaseUrl(), '/api/er/csrf');

let accessToken: string | null = null;

function normalizePath(url?: string) {
  if (!url) return '';

  try {
    const parsedUrl = new URL(url, window.location.origin);
    return parsedUrl.pathname;
  } catch {
    return url.startsWith('/') ? url : `/${url}`;
  }
}

function unwrapEnvelope<T>(envelope: ApiEnvelope<T>, fallbackMessage: string) {
  if (envelope.success) return envelope.result ?? null;

  const message =
    envelope.errorMessages?.join(', ') || envelope.errorCodes?.join(', ') || fallbackMessage;
  throw new Error(message);
}

export function setAuthAccessToken(token: string | null) {
  accessToken = token?.trim() || null;
}

export function clearAuthAccessToken() {
  accessToken = null;
}

export function attachBearerToken(config: InternalAxiosRequestConfig) {
  const normalizedPath = normalizePath(config.url);
  const needsBearerToken = PROTECTED_ER_PATHS.some((path) => normalizedPath.startsWith(path));

  if (!needsBearerToken || !accessToken) return config;

  const headers = AxiosHeaders.from(config.headers);
  headers.set('Authorization', `Bearer ${accessToken}`);
  config.headers = headers;
  return config;
}

authAxiosInstance.interceptors.request.use(attachBearerToken);

export async function fetchCurrentUser(token?: string | null): Promise<ErisUser | null> {
  const resolvedToken = token?.trim() || accessToken;
  const response = resolvedToken
    ? await authAxiosInstance.get<ApiEnvelope<ErisUser>>('/api/er/users/me', {
        headers: {
          Authorization: `Bearer ${resolvedToken}`,
        },
      })
    : await authAxiosInstance.get<ApiEnvelope<ErisUser>>('/api/er/users/me');
  return unwrapEnvelope(response.data, 'Failed to load current user.');
}

export async function postBackendLogout() {
  await authAxiosInstance.post('/api/er/logout');
}

export async function callProtectedApi<T>(url: string) {
  const response = await authAxiosInstance.get<ApiEnvelope<T>>(url);
  return unwrapEnvelope(response.data, `Failed to load protected resource from ${url}.`);
}

export async function callProtectedRaw(url: string) {
  const requestConfig = {
    baseURL: getApiBaseUrl(),
    withCredentials: true,
  } as const;

  if (!accessToken) {
    return await axios.get(url, requestConfig);
  }

  return await axios.get(url, {
    ...requestConfig,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
}
